import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../axiosConfig";
import PartPickerModal from "../../pickers/PartPickerModal";

export default function GrupaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [bxcod, setBxcod] = useState("");
  const [bxdescr, setBxdescr] = useState("");
  const [linii, setLinii] = useState([]); // [{ bxid, bxpart_id, part_code, part_name }]

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  const [partPickerOpen, setPartPickerOpen] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadGroup = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/contor/grupe/${id}`);
      const data = res.data;
      setBxcod(data.bxcod);
      setBxdescr(data.bxdescr);
      setLinii(data.linii || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la încărcarea grupei.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!bxcod.trim()) errs.bxcod = "Codul este obligatoriu.";
    if (!bxdescr.trim()) errs.bxdescr = "Denumirea este obligatorie.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePickPart = (part) => {
    // part is from PartPickerModal: { id, label, raw: Bxpart object }
    const partId = part.id;
    
    // Check duplicate
    if (linii.some((l) => l.bxpart_id === partId)) {
      toast.warning("Această piesă este deja adăugată în listă.");
      return;
    }

    setLinii([
      ...linii,
      {
        bxid: null,
        bxpart_id: partId,
        part_code: part.raw?.cod || part.label?.split(" • ")[0] || "",
        part_name: part.raw?.denumire || part.label?.split(" • ")[1] || "",
      },
    ]);
  };

  const handleRemoveLine = (index) => {
    setLinii(linii.filter((_, idx) => idx !== index));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError("");

    const payload = {
      bxcod: bxcod.trim(),
      bxdescr: bxdescr.trim(),
      linii: linii.map((l) => ({
        bxid: l.bxid,
        bxpart_id: l.bxpart_id,
      })),
    };

    try {
      if (isEdit) {
        await api.put(`/contor/grupe/${id}`, payload);
        toast.success("Grupa de echipamente a fost actualizată.");
      } else {
        await api.post("/contor/grupe", payload);
        toast.success("Grupa de echipamente a fost creată.");
      }
      navigate("/contor/grupe");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Se încarcă datele grupei...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 text-dark fw-bold">
            {isEdit ? `Editează Grupa: ${bxcod}` : "Adaugă Grupă Echipamente"}
          </h2>
          <p className="text-muted mb-0">
            {isEdit ? "Modifică detaliile și compatibilitățile grupei" : "Definirea unei grupe noi și a pieselor compatibile"}
          </p>
        </div>
        <Button variant="outline-secondary" onClick={() => navigate("/contor/grupe")}>
          Înapoi la Listă
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSave}>
        <Row>
          <Col xs={12} lg={4}>
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 text-dark">Date Generale</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Cod Grupă <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={bxcod}
                    onChange={(e) => {
                      setBxcod(e.target.value);
                      if (errors.bxcod) setErrors({ ...errors, bxcod: null });
                    }}
                    isInvalid={!!errors.bxcod}
                    placeholder="ex: IMPRIMANTE"
                  />
                  <Form.Control.Feedback type="invalid">{errors.bxcod}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Denumire/Descriere <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={bxdescr}
                    onChange={(e) => {
                      setBxdescr(e.target.value);
                      if (errors.bxdescr) setErrors({ ...errors, bxdescr: null });
                    }}
                    isInvalid={!!errors.bxdescr}
                    placeholder="ex: Echipamente de printare laser"
                  />
                  <Form.Control.Feedback type="invalid">{errors.bxdescr}</Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={8}>
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-light fw-bold py-3 d-flex justify-content-between align-items-center">
                <span className="text-dark">Piese / Modele Echipamente Corelate</span>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => setPartPickerOpen(true)}
                >
                  + Adaugă Piesă
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "150px" }}>Cod Piesă</th>
                      <th>Denumire/Descriere Model</th>
                      <th style={{ width: "100px" }} className="text-end">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linii.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-muted text-center py-4">
                          Nu a fost asociată nicio piesă compatibilă. Echipamentul compatibil se va alege prin selectarea reperelor din catalogul de produse.
                        </td>
                      </tr>
                    ) : (
                      linii.map((l, idx) => (
                        <tr key={idx}>
                          <td className="fw-bold">{l.part_code}</td>
                          <td>{l.part_name}</td>
                          <td className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveLine(idx)}
                            >
                              Șterge
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-end gap-2 mb-5">
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/contor/grupe")}
                disabled={saving}
              >
                Renunță
              </Button>
              <Button variant="dark" type="submit" disabled={saving}>
                {saving ? "Se salvează..." : "Salvează Grupa"}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <PartPickerModal
        show={partPickerOpen}
        onHide={() => setPartPickerOpen(false)}
        onPick={handlePickPart}
        title="Selectează Piesă/Model Echipament"
      />
    </Container>
  );
}
